import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { BankConnectionResult, GlobalErrorType } from '~/enums';
import { logToSentry } from '~/helpers/loggers.helper';
import { parseLanguage } from '~/helpers/market.helper';
import { useAppDispatch } from '~/hooks/useAppDispatch';
import useQueryParams from '~/hooks/useQueryParams';
import { BANK_CONNECTION_RESULT_QUERY_STRING_KEY } from '~/pages/UpdateApplicationStandalone/constants';
import { RootState } from '~/store';
import { useGetClientDataQuery } from '~/store/apis/endpoints/myPagesUnauthenticatedApi/clientData/clientData.api';
import { toggleGlobalErrorOn } from '~/store/slices/globalError.slice';
import { setLanguage } from '~/store/slices/intl.slice';

export const useBankConnectionStandalone = () => {
  const { market, languageIsSet } = useSelector(
    (state: RootState) => state.intl
  );

  const { qcode: qCodeParam = '' } = useSelector(
    (state: RootState) => state.updateApplication
  );

  const dispatch = useAppDispatch();
  const queryParams = useQueryParams();
  const bankConnectionResultParam = queryParams.get(
    BANK_CONNECTION_RESULT_QUERY_STRING_KEY
  );
  const languageParam = queryParams.get('language');
  const history = useHistory();
  const [error, setError] = useState<boolean>(
    bankConnectionResultParam === BankConnectionResult.Error ||
      bankConnectionResultParam === BankConnectionResult.Cancelled
  );
  const [connectedSuccessfully] = useState<boolean>(
    bankConnectionResultParam === BankConnectionResult.Success
  );
  const parsedLanguageParam = parseLanguage(languageParam);
  const {
    data: clientData,
    isFetching: isFetchingClientData,
    isError,
  } = useGetClientDataQuery(
    {
      qCode: qCodeParam,
      market,
    },
    {
      skip: connectedSuccessfully || !qCodeParam,
    }
  );

  useEffect(() => {
    const currentUrl = new URL(globalThis.location.href);
    currentUrl.searchParams.delete('language');
    history.replace({ search: currentUrl.search });
    if (parsedLanguageParam) {
      dispatch(setLanguage(parsedLanguageParam));
    }
  }, []);

  useEffect(() => {
    if (!qCodeParam) {
      logToSentry(
        new Error('qCodeParam is required'),
        'Missing qCodeParam in BankConnectionStandalone'
      );
    }

    if (isError || !qCodeParam) {
      dispatch(toggleGlobalErrorOn(GlobalErrorType.API_FAILURE));
    }
  }, [isError]);

  const onCheckApplicationStatusButtonClick = () => {
    history.push('/');
  };

  return {
    connectedSuccessfully,
    qCodeParam,
    isError,
    clientData,
    isFetchingClientData,
    languageIsSet,
    error,
    setError,
    onCheckApplicationStatusButtonClick,
  };
};
